package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import dev.lingvo.shared.CaptureDto
import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.ShotStoreImportDto
import dev.lingvo.shared.ShotStoreImportRequest
import dev.lingvo.shared.StorePlatform
import dev.lingvo.shared.StoreShotsDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.importScreenshotStoreImages
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.api.screenshotCaptures
import dev.lingvo.site.api.screenshotStoreImages
import dev.lingvo.site.ds.AsyncFailureState
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCheckbox
import dev.lingvo.site.ds.LConfirmDialog
import dev.lingvo.site.ds.LConfirmTone
import dev.lingvo.site.ds.LModal
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LoadingState
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.state.ProjectActionClaim
import dev.lingvo.site.state.ProjectPrivilege
import dev.lingvo.site.state.authoritySubject
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectResource
import kotlinx.coroutines.awaitCancellation
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img

/** The read and import stay within the live project/session claims used by the rest of Screenshots. */
@Composable
fun StoreImport(slug: String, platform: StorePlatform, onClose: () -> Unit, onImported: () -> Unit) {
    val subject = liveProjectRoute(slug)?.authoritySubject(Session.organizations)
    var retry by remember { mutableStateOf(0) }
    val claim = remember(subject, platform) { ProjectActionClaim.capture(liveProjectRoute(slug), Session.organizations, ProjectPrivilege.STRUCTURE) }
    val scope = rememberCoroutineScope()
    val actions = remember(subject, platform) { ScreenshotsActionRunner(slug, { scope.launch { it() } }) }
    var busy by remember(subject, platform) { mutableStateOf(false) }
    var error by remember(subject, platform) { mutableStateOf<String?>(null) }
    var result by remember(subject, platform) { mutableStateOf<ShotStoreImportDto?>(null) }
    val loaded = loadReloadable(subject, platform, reloadToken = retry) {
        val live = subject ?: awaitCancellation()
        requestProjectResource(live.action, slug, Session.organizations) {
            // This dialog imports position-based screenshots as captures, not fixed listing artwork.
            ApiClient.screenshotStoreImages(slug, platform, devices = DeviceClass.forPlatform(platform)
                .filterNot { it == DeviceClass.PLAY_FEATURE_GRAPHIC }) to ApiClient.screenshotCaptures(slug, platform)
        }
    }
    LModal(true, t("app.projects.slug.screenshots.store_import.start_from_your_current_store_screenshots", "Start from your current store screenshots"), { if (!busy) onClose() }) {
        when (val state = loaded.state) {
            Async.Loading -> LoadingState(rows = 3)
            is Async.Failure -> AsyncFailureState(state, subject = t("app.projects.slug.screenshots.store_import.the_store_screenshots", "the store screenshots"), onRetry = { retry++ })
            is Async.Success -> StoreImportPreview(state.value.first, state.value.second, busy, error, result) { request ->
                actions.run(ScreenshotsPrivilege.APPROVE, { busy = it }, {
                    submitStoreImport(state.value.first, request,
                        { claim?.remainsAuthorized(liveProjectRoute(slug), Session.organizations) == true },
                        { ApiClient.screenshotCaptures(slug, platform) },
                        { ApiClient.importScreenshotStoreImages(slug, platform, it) })
                }, { failure ->
                    error = if (failure is StoreImportSelectionBlocked) tr("app.projects.slug.screenshots.store_import.these_languages_and_devices_already_have", "These languages and devices already have captures. Choose empty cells to avoid replacing them.")
                        else if (failure is ApiException && failure.code == "screenshot_quota_exceeded") screenshotsQuotaExceededMessage(failure.details)
                        else shotRequestFailure(failure)
                }, { result = it; error = null; onImported() })
            }
        }
    }
}

/** Readable sets and failures share a preview; choosing slot creation is never implicit. */
@Composable
fun StoreImportPreview(
    shots: StoreShotsDto, captures: List<CaptureDto>, busy: Boolean, error: String?, result: ShotStoreImportDto?,
    onImport: (ShotStoreImportRequest) -> Unit,
) {
    var locales by remember(shots) { mutableStateOf(shots.sets.filter { it.readable && it.images.isNotEmpty() }.map { it.locale }.toSet()) }
    var devices by remember(shots) { mutableStateOf(shots.sets.filter { it.readable }.map { it.deviceClass }.toSet()) }
    var createSlots by remember { mutableStateOf(false) }
    var confirming by remember { mutableStateOf(false) }
    val selection = storeImportSelection(shots, locales, devices, createSlots, captures)
    Column(Modifier.gap(Space.MD).styleModifier { property("min-width", "0"); property("overflow-wrap", "anywhere") }) {
        shots.stateNote?.let { LAlert(it, variant = LAlertVariant.INFO) }
        LMuted(t("app.projects.slug.screenshots.store_import.import_the_images_currently_on_your", "Import the images currently on your store page as captures. Existing captures block import for their language and device. Your live store is unchanged."))
        if (shots.sets.isEmpty()) LAlert(t("app.projects.slug.screenshots.store_import.no_current_store_screenshots_were_found", "No current store screenshots were found."))
        shots.sets.map { it.locale }.distinct().forEach { locale ->
            LCheckbox(locale in locales, { checked -> locales = if (checked) locales + locale else locales - locale }, locale,
                enabled = !busy && result == null && shots.sets.any { it.locale == locale && it.readable && it.images.isNotEmpty() })
            shots.sets.filter { it.locale == locale }.forEach { set ->
                LMuted(tf("shots.import.set", "{locale} · {device} · {count} images", "locale" to set.storeLocale, "device" to screenshotDeviceLabel(set.deviceClass), "count" to set.images.size))
                if (!set.readable) LAlert(set.note ?: t("app.projects.slug.screenshots.store_import.these_store_images_could_not_be", "These store images could not be read."), variant = LAlertVariant.WARNING)
                Div(Modifier.styleModifier { property("display", "flex"); property("gap", Space.SM.toString()); property("overflow-x", "auto"); property("max-width", "100%") }.toAttrs()) {
                    set.images.sortedBy { it.position }.forEach { image ->
                        val safe = image.previewUrl?.takeIf { it.startsWith("https://") }
                        if (safe != null) Img(safe, tf("shots.import.preview", "{locale}, screenshot {position}", "locale" to locale, "position" to image.position + 1), attrs = {
                            attr("referrerpolicy", "no-referrer"); attr("loading", "lazy")
                            style { property("max-height", "160px"); property("max-width", "100%"); property("object-fit", "contain") }
                        }) else LMuted(tf("shots.import.no_preview", "Screenshot {position}: preview unavailable", "position" to image.position + 1))
                    }
                }
            }
        }
        shots.sets.map { it.deviceClass }.distinct().forEach { device ->
            LCheckbox(device in devices, { checked -> devices = if (checked) devices + device else devices - device }, screenshotDeviceLabel(device), enabled = !busy && result == null)
        }
        Div(attrs = { attr("data-create-slots", "") }) {
            LCheckbox(createSlots, { createSlots = it }, t("app.projects.slug.screenshots.store_import.create_missing_plan_slots_for_these", "Create missing plan slots for these screenshots"), enabled = !busy && result == null)
        }
        if (!createSlots) LMuted(t("app.projects.slug.screenshots.store_import.only_matching_plan_slots_will_be", "Only matching plan slots will be used. Images without a slot will be skipped."))
        if (selection.occupied) LAlert(t("app.projects.slug.screenshots.store_import.these_languages_and_devices_already_have", "These languages and devices already have captures. Choose empty cells to avoid replacing them."), variant = LAlertVariant.WARNING)
        if (selection.imageCount == 0) LMuted(t("app.projects.slug.screenshots.store_import.select_a_readable_language_and_device", "Select a readable language and device with images to import."))
        error?.let { LAlert(it, variant = LAlertVariant.DANGER, live = true) }
        result?.let { imported ->
            Div(attrs = { attr("role", "status") }) {
                LMuted(tf("shots.import.result", "Imported {count} captures; skipped {skipped}. Created slots: {slots}.", "count" to imported.imported,
                    "skipped" to imported.skipped.size, "slots" to imported.createdSlots.joinToString(", ").ifEmpty { "—" }))
                imported.skipped.forEach { skip -> LMuted(tf("shots.import.skip", "{locale} · {device} · screenshot {position}: {reason}",
                    "locale" to skip.locale, "device" to screenshotDeviceLabel(skip.deviceClass), "position" to skip.position + 1, "reason" to skip.reason)) }
            }
        }
        LButton(t("app.projects.slug.screenshots.store_import.import_selected_screenshots", "Import selected screenshots"), { confirming = true }, variant = LButtonVariant.SECONDARY,
            enabled = !selection.blocked && !busy && result == null, loading = busy,
            modifier = Modifier.attr("data-store-import-start", "").styleModifier { property("max-width", "100%"); property("white-space", "normal") },
        )
    }
    LConfirmDialog(confirming, t("app.projects.slug.screenshots.store_import.import_these_store_screenshots", "Import these store screenshots?"), t("app.projects.slug.screenshots.store_import.import_captures", "Import captures"),
        { if (!selection.blocked && !busy && result == null) onImport(selection.request) }, { confirming = false }, tone = LConfirmTone.NEUTRAL,
        consequenceTitle = t("app.projects.slug.screenshots.store_import.your_store_stays_as_it_is", "Your store stays as it is"),
        consequence = tf("shots.import.consequence", "Adds up to {count} captures to empty cells in Lingvo. Existing captures are checked again before import. Missing slots are created only if you chose that option.", "count" to selection.imageCount),
        busy = busy, error = error)
    // A completed import cannot be submitted twice, even if the dialog was left open.
    androidx.compose.runtime.LaunchedEffect(result) { if (result != null) confirming = false }
}
